import { Observable, of } from 'rxjs';

import {
    ChatHistoryPageQuery,
    ChatManagerState,
    ChatMessageSearchItem,
    ChatOutgoingMessage,
    ChatPageHasMore,
    ChatSessionItem,
} from '../../types/chat-session.types';
import { IChatTemplate } from '../../types/chat-template.types';
import { BaseChatSession } from '../sessions/base-chat.session';

/** Абстрактная база для менеджера одного режима чата. */
export abstract class BaseChatManager {
    /** Человекочитаемая метка режима. */
    public abstract readonly label: string;

    /** Иконка вкладки режима. */
    public abstract readonly icon?: string;

    /** Список сессий (lightweight previews) — только текущая страница. */
    public abstract readonly sessions$: Observable<ChatSessionItem[]>;

    /**
     * Есть ли соседние страницы списка сессий.
     * `before` — более старые (кнопка снизу), `after` — ранее показанные более новые (кнопка сверху).
     */
    public readonly sessionsHasMore$: Observable<ChatPageHasMore> = of({ before: false, after: false });

    /** Идёт ли запрос соседней страницы списка сессий. */
    public readonly sessionsIsLoadingMore$: Observable<ChatPageHasMore> = of({ before: false, after: false });

    /** Открытая сессия = живое соединение. null если сессия не открыта. */
    public abstract readonly activeSession$: Observable<BaseChatSession | null>;

    /** Состояние менеджера: загрузка, готовность и ошибка уровня менеджера. */
    public abstract readonly state$: Observable<ChatManagerState>;

    /**
     * Суммарное количество непрочитанных по всем сессиям режима.
     * Реализация сама определяет стратегию (один запрос, polling, push и т.д.).
     */
    public abstract readonly totalUnreadCount$: Observable<number>;

    /** Шаблон отображения: реестр компонентов и конфигурация UI. */
    public abstract readonly template: IChatTemplate;

    /**
     * Открыть сессию — создать ChatSession, обновить activeSession$.
     * `historyQuery` задаёт страницу истории (хвост, если не передан).
     */
    public abstract openSession(sessionId: string, historyQuery?: ChatHistoryPageQuery): void;

    /**
     * Сменить страницу списка сессий.
     * `before` — более старые, `after` — вернуть предыдущую страницу.
     * Реализации без пагинации оставляют no-op.
     */
    public loadMoreSessions(_direction: 'before' | 'after'): void {}

    /** Закрыть активную сессию — destroy + activeSession$ = null. */
    public abstract closeSession(): void;

    /** Удалить сессию по ID и при необходимости закрыть её, если она активна. */
    public abstract deleteSession(sessionId: string): void;

    /**
     * Создать новую сессию и сразу открыть её.
     * Если передано стартовое сообщение, оно должно отправиться после успешного создания сессии.
     */
    public abstract createSession(initialMessage?: ChatOutgoingMessage): void;

    /** Поиск сессий по строке запроса. */
    public abstract searchSessions(query: string): Observable<ChatSessionItem[]>;

    /** Поиск сообщений по строке запроса. */
    public abstract searchMessages(query: string): Observable<ChatMessageSearchItem[]>;

    /** Открыть сессию и прокрутить к конкретному сообщению. */
    public abstract openSessionAtMessage(item: ChatMessageSearchItem): void;

    /**
     * Запускает менеджер: подписки, загрузка данных.
     * Вызывается из ChatController сразу после создания.
     */
    public abstract init(): void;

    /** Полный снос: все подписки, соединения, состояние. Вызывается только из ChatController. */
    public abstract destroy(): void;
}
